import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { refreshSettings } from "@/hooks/useSettings";
import { Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";

type FooterLink = { label: string; url: string };
type FooterSection = { enabled: boolean; heading: string; links: FooterLink[] };
type ImageItem = { name: string; image_url: string };
type SocialItem = { platform: string; url: string };

type FooterConfig = {
  logo_url: string;
  brand_description: string;
  sections: {
    information: FooterSection;
    quick: FooterSection;
    profile: FooterSection;
  };
  contact: { enabled: boolean; heading: string; phone: string; email: string; address: string };
  payments: { enabled: boolean; heading: string; items: ImageItem[] };
  couriers: { enabled: boolean; heading: string; items: ImageItem[] };
  socials: { enabled: boolean; items: SocialItem[] };
  copyright: string;
};

const DEFAULT_CONFIG: FooterConfig = {
  logo_url: "",
  brand_description: "Honest products, honest prices. Delivered across Bangladesh.",
  sections: {
    information: {
      enabled: true,
      heading: "Information",
      links: [
        { label: "Delivery", url: "/delivery" },
        { label: "Privacy Policy", url: "/privacy" },
        { label: "Return Policy", url: "/return-policy" },
      ],
    },
    quick: {
      enabled: true,
      heading: "Quick Links",
      links: [
        { label: "Home", url: "/" },
        { label: "Contact", url: "/contact" },
        { label: "Wishlist", url: "/wishlist" },
      ],
    },
    profile: {
      enabled: true,
      heading: "Profile",
      links: [
        { label: "Login", url: "/auth" },
        { label: "Register", url: "/auth" },
        { label: "Order Tracking", url: "/orders" },
      ],
    },
  },
  contact: { enabled: true, heading: "Contact Us", phone: "", email: "", address: "" },
  payments: { enabled: true, heading: "Payment Methods", items: [] },
  couriers: { enabled: true, heading: "Shipping Partners", items: [] },
  socials: {
    enabled: true,
    items: [
      { platform: "facebook", url: "" },
      { platform: "instagram", url: "" },
      { platform: "whatsapp", url: "" },
    ],
  },
  copyright: "",
};

const SOCIAL_PLATFORMS = [
  "facebook", "instagram", "whatsapp", "messenger",
  "twitter", "youtube", "linkedin", "telegram",
];

const uploadImage = async (file: File, prefix: string): Promise<string> => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "png";
  const path = `footer/${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
  const { error } = await supabase.storage.from("site-assets").upload(path, file, {
    cacheControl: "3600",
    upsert: true,
    contentType: file.type,
  });
  if (error) throw error;
  return supabase.storage.from("site-assets").getPublicUrl(path).data.publicUrl;
};

const ImageUploadButton = ({
  onUploaded,
  prefix,
  label = "Upload",
}: {
  onUploaded: (url: string) => void;
  prefix: string;
  label?: string;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > 2 * 1024 * 1024) {
            toast.error("Image must be under 2 MB");
            return;
          }
          setBusy(true);
          try {
            const url = await uploadImage(f, prefix);
            onUploaded(url);
            toast.success("Image uploaded");
          } catch (err: any) {
            toast.error(err.message || "Upload failed");
          } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
          }
        }}
      />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy}>
        <Upload className="h-3 w-3" /> {busy ? "Uploading..." : label}
      </Button>
    </>
  );
};

const SectionEditor = ({
  title,
  section,
  onChange,
}: {
  title: string;
  section: FooterSection;
  onChange: (s: FooterSection) => void;
}) => {
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Enabled</Label>
          <Switch checked={section.enabled} onCheckedChange={(v) => onChange({ ...section, enabled: v })} />
        </div>
      </div>
      <Input
        value={section.heading}
        placeholder="Heading"
        onChange={(e) => onChange({ ...section, heading: e.target.value })}
        className="font-medium"
      />
      <div className="space-y-2">
        {section.links.map((l, j) => (
          <div key={j} className="flex items-center gap-2">
            <Input
              value={l.label}
              placeholder="Label"
              onChange={(e) =>
                onChange({
                  ...section,
                  links: section.links.map((x, i) => (i === j ? { ...x, label: e.target.value } : x)),
                })
              }
            />
            <Input
              value={l.url}
              placeholder="/path or https://..."
              onChange={(e) =>
                onChange({
                  ...section,
                  links: section.links.map((x, i) => (i === j ? { ...x, url: e.target.value } : x)),
                })
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onChange({ ...section, links: section.links.filter((_, i) => i !== j) })}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange({ ...section, links: [...section.links, { label: "", url: "" }] })}
        >
          <Plus className="h-3 w-3" /> Add link
        </Button>
      </div>
    </div>
  );
};

const ImageItemList = ({
  items,
  onChange,
  prefix,
}: {
  items: ImageItem[];
  onChange: (items: ImageItem[]) => void;
  prefix: string;
}) => (
  <div className="space-y-2">
    {items.map((item, i) => (
      <div key={i} className="flex items-center gap-2 rounded-md border border-border p-2">
        <div className="h-12 w-16 grid place-items-center rounded bg-muted shrink-0 overflow-hidden">
          {item.image_url ? (
            <img src={item.image_url} alt={item.name} className="h-full w-full object-contain" />
          ) : (
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <Input
          value={item.name}
          placeholder="Name (e.g. bKash)"
          onChange={(e) => onChange(items.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
        />
        <ImageUploadButton
          prefix={prefix}
          label={item.image_url ? "Replace" : "Logo"}
          onUploaded={(url) =>
            onChange(items.map((x, idx) => (idx === i ? { ...x, image_url: url } : x)))
          }
        />
        <Button type="button" variant="ghost" size="icon" onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ))}
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => onChange([...items, { name: "", image_url: "" }])}
    >
      <Plus className="h-3 w-3" /> Add
    </Button>
  </div>
);

const AdminFooter = () => {
  const [cfg, setCfg] = useState<FooterConfig>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("settings").select("value").eq("key", "footer_config").maybeSingle().then(({ data }) => {
      if (data?.value && typeof data.value === "object") {
        setCfg({ ...DEFAULT_CONFIG, ...(data.value as any) });
      }
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from("settings").upsert({ key: "footer_config", value: cfg as any });
      if (error) throw error;
      toast.success("Footer saved");
      refreshSettings();
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-3xl font-bold">Footer</h1>
        <p className="text-muted-foreground">Manage every section of the storefront footer</p>
      </div>

      {/* Brand */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Brand</h2>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-xl border-2 border-dashed bg-muted/40 grid place-items-center overflow-hidden shrink-0">
            {cfg.logo_url ? (
              <img src={cfg.logo_url} alt="Footer logo" className="h-full w-full object-contain" />
            ) : (
              <ImageIcon className="h-7 w-7 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <ImageUploadButton
              prefix="logo"
              label={cfg.logo_url ? "Replace logo" : "Upload logo"}
              onUploaded={(url) => setCfg({ ...cfg, logo_url: url })}
            />
            {cfg.logo_url && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setCfg({ ...cfg, logo_url: "" })}>
                Remove
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label>Brand description</Label>
          <Textarea
            rows={3}
            value={cfg.brand_description}
            onChange={(e) => setCfg({ ...cfg, brand_description: e.target.value })}
          />
        </div>
      </Card>

      {/* Link sections */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Link sections</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <SectionEditor
            title="Information"
            section={cfg.sections.information}
            onChange={(s) => setCfg({ ...cfg, sections: { ...cfg.sections, information: s } })}
          />
          <SectionEditor
            title="Quick Links"
            section={cfg.sections.quick}
            onChange={(s) => setCfg({ ...cfg, sections: { ...cfg.sections, quick: s } })}
          />
          <SectionEditor
            title="Profile"
            section={cfg.sections.profile}
            onChange={(s) => setCfg({ ...cfg, sections: { ...cfg.sections, profile: s } })}
          />
        </div>
      </Card>

      {/* Contact */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Contact</h2>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enabled</Label>
            <Switch
              checked={cfg.contact.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, contact: { ...cfg.contact, enabled: v } })}
            />
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Heading</Label>
            <Input
              value={cfg.contact.heading}
              onChange={(e) => setCfg({ ...cfg, contact: { ...cfg.contact, heading: e.target.value } })}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={cfg.contact.phone}
              placeholder="+880 1XXXXXXXXX"
              onChange={(e) => setCfg({ ...cfg, contact: { ...cfg.contact, phone: e.target.value } })}
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={cfg.contact.email}
              placeholder="hello@example.com"
              onChange={(e) => setCfg({ ...cfg, contact: { ...cfg.contact, email: e.target.value } })}
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={cfg.contact.address}
              placeholder="Dhaka, Bangladesh"
              onChange={(e) => setCfg({ ...cfg, contact: { ...cfg.contact, address: e.target.value } })}
            />
          </div>
        </div>
      </Card>

      {/* Payments */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Payment methods</h2>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enabled</Label>
            <Switch
              checked={cfg.payments.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, payments: { ...cfg.payments, enabled: v } })}
            />
          </div>
        </div>
        <Input
          value={cfg.payments.heading}
          placeholder="Heading"
          onChange={(e) => setCfg({ ...cfg, payments: { ...cfg.payments, heading: e.target.value } })}
        />
        <ImageItemList
          prefix="pay"
          items={cfg.payments.items}
          onChange={(items) => setCfg({ ...cfg, payments: { ...cfg.payments, items } })}
        />
      </Card>

      {/* Couriers */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Shipping partners</h2>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enabled</Label>
            <Switch
              checked={cfg.couriers.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, couriers: { ...cfg.couriers, enabled: v } })}
            />
          </div>
        </div>
        <Input
          value={cfg.couriers.heading}
          placeholder="Heading"
          onChange={(e) => setCfg({ ...cfg, couriers: { ...cfg.couriers, heading: e.target.value } })}
        />
        <ImageItemList
          prefix="courier"
          items={cfg.couriers.items}
          onChange={(items) => setCfg({ ...cfg, couriers: { ...cfg.couriers, items } })}
        />
      </Card>

      {/* Socials */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">Social links</h2>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enabled</Label>
            <Switch
              checked={cfg.socials.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, socials: { ...cfg.socials, enabled: v } })}
            />
          </div>
        </div>
        <div className="space-y-2">
          {cfg.socials.items.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                value={s.platform}
                onChange={(e) =>
                  setCfg({
                    ...cfg,
                    socials: {
                      ...cfg.socials,
                      items: cfg.socials.items.map((x, idx) => (idx === i ? { ...x, platform: e.target.value } : x)),
                    },
                  })
                }
                className="h-10 rounded-md border border-input bg-background px-3 text-sm capitalize"
              >
                {SOCIAL_PLATFORMS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <Input
                value={s.url}
                placeholder="https://..."
                onChange={(e) =>
                  setCfg({
                    ...cfg,
                    socials: {
                      ...cfg.socials,
                      items: cfg.socials.items.map((x, idx) => (idx === i ? { ...x, url: e.target.value } : x)),
                    },
                  })
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                  setCfg({ ...cfg, socials: { ...cfg.socials, items: cfg.socials.items.filter((_, idx) => idx !== i) } })
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setCfg({
                ...cfg,
                socials: { ...cfg.socials, items: [...cfg.socials.items, { platform: "facebook", url: "" }] },
              })
            }
          >
            <Plus className="h-3 w-3" /> Add social
          </Button>
        </div>
      </Card>

      {/* Copyright */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Copyright</h2>
        <Input
          value={cfg.copyright}
          placeholder="© 2026 MyBrand. All rights reserved."
          onChange={(e) => setCfg({ ...cfg, copyright: e.target.value })}
        />
      </Card>

      <div className="sticky bottom-4 z-10">
        <Button variant="hero" size="lg" onClick={save} disabled={saving} className="w-full sm:w-auto">
          {saving ? "Saving..." : "Save footer"}
        </Button>
      </div>
    </div>
  );
};

export default AdminFooter;
